import { useAuth } from '@/auth/useAuth';
import { AudioPlayer } from '@/components/audio/AudioPlayer';
import { Button } from '@/components/ui';
import { Sello, estadoAudio } from '@/components/hallazgos/Sello';
import { type AudioDuplicadoItemDTO, sysadminService } from '@/services/sysadmin.service';
import { ChevronDown, ChevronRight, Copy, Download, ExternalLink, FileUp, Headphones, RefreshCw, Search } from 'lucide-react';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Revisión de audios presuntamente duplicados. De las coincidencias por duración de la plataforma
 * se dejan solo las probables: audios vigentes con la misma duración que además son de la misma
 * empresa o los subió el mismo asesor. Quien revisa escucha cada caso y decide; no se toca la base
 * de datos. Las decisiones se guardan en este navegador y se exportan como JSONL.
 */

type Decision = 'PENDIENTE' | 'DUPLICADO' | 'NO_DUPLICADO' | 'REVISAR';

interface Caso {
  /** audioIds ordenados: identifica el caso entre recargas. */
  clave: string;
  motivo: 'MISMA_EMPRESA' | 'MISMO_ASESOR';
  duracionFormatted: string;
  audios: AudioDuplicadoItemDTO[];
}

interface Seguimiento {
  decision: Decision;
  nota?: string;
  actualizadoEn?: string;
}

const STORAGE_KEY = 'issues.audiosDuplicados';
const DECISIONES: { valor: Decision; label: string; variant: 'yellow' | 'red' | 'green' | 'blue' }[] = [
  { valor: 'PENDIENTE', label: 'Pendiente', variant: 'yellow' },
  { valor: 'DUPLICADO', label: 'Es duplicado', variant: 'red' },
  { valor: 'NO_DUPLICADO', label: 'No es duplicado', variant: 'green' },
  { valor: 'REVISAR', label: 'Revisar', variant: 'blue' },
];
const VIGENTE = (estado: string) => estado !== 'ELIMINADO' && estado !== 'RECHAZADO';
const actividad = (orden?: number | null) => (orden == null ? null : orden === 1 ? 'Diagnóstico' : `Asesoría ${orden - 1}`);
const fecha = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString('es-CO') : '—');

function leerGuardado(): Record<string, Seguimiento> {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}') as Record<string, Seguimiento>;
  } catch {
    return {};
  }
}

function guardar(seg: Record<string, Seguimiento>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(seg));
  } catch {
    // Sin almacenamiento (modo privado, cuota): la vista sigue funcionando en memoria.
  }
}

/** Arma los casos probables a partir de los grupos de misma duración. */
function casosProbables(grupos: { duracionFormatted: string; audios: AudioDuplicadoItemDTO[] }[]): Caso[] {
  const casos = new Map<string, Caso>();
  for (const g of grupos) {
    const vigentes = g.audios.filter((a) => VIGENTE(a.estado));
    for (const [motivo, campo] of [
      ['MISMA_EMPRESA', 'empresaId'],
      ['MISMO_ASESOR', 'asesorId'],
    ] as const) {
      const porValor = new Map<string | number, AudioDuplicadoItemDTO[]>();
      for (const a of vigentes) porValor.set(a[campo], [...(porValor.get(a[campo]) ?? []), a]);
      for (const audios of porValor.values()) {
        if (audios.length < 2) continue;
        const clave = audios
          .map((a) => a.audioId)
          .sort((x, y) => x - y)
          .join('-');
        // Si el mismo conjunto sale por empresa y por asesor, queda una sola vez (por empresa).
        if (!casos.has(clave)) casos.set(clave, { clave, motivo, duracionFormatted: g.duracionFormatted, audios });
      }
    }
  }
  return [...casos.values()];
}

export function AudiosDuplicadosRevisionPage() {
  const esSysadmin = useAuth().user?.asesor?.rol === 'SYSADMIN';
  const [casos, setCasos] = useState<Caso[] | null>(null);
  const [ordenPorAsesoria, setOrdenPorAsesoria] = useState<Map<number, number | null>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [cargando, setCargando] = useState(false);
  const [seg, setSeg] = useState<Record<string, Seguimiento>>(leerGuardado);
  const [filtro, setFiltro] = useState<Decision | 'TODOS'>('TODOS');
  const [busqueda, setBusqueda] = useState('');
  const [abiertos, setAbiertos] = useState<Set<string>>(new Set());
  const inputRef = useRef<HTMLInputElement>(null);

  const cargar = () => {
    setCargando(true);
    setError(null);
    Promise.all([sysadminService.obtenerAudiosDuplicados(), sysadminService.listarAsesorias()])
      .then(([dup, { asesorias }]) => {
        setCasos(casosProbables(dup.grupos));
        setOrdenPorAsesoria(new Map(asesorias.map((a) => [a.asesoriaId, a.orden])));
      })
      .catch((e: any) => setError(`No se pudieron cargar los audios: ${e.message}`))
      .finally(() => setCargando(false));
  };
  useEffect(cargar, []);

  const cambiar = (clave: string, cambios: Partial<Seguimiento>) => {
    const nuevo = {
      ...seg,
      [clave]: {
        ...seg[clave],
        ...cambios,
        decision: cambios.decision ?? seg[clave]?.decision ?? 'PENDIENTE',
        actualizadoEn: new Date().toISOString(),
      },
    };
    setSeg(nuevo);
    guardar(nuevo);
  };

  const exportar = () => {
    if (!casos) return;
    const lineas = casos.map((c) => JSON.stringify({ ...c, ...(seg[c.clave] ?? { decision: 'PENDIENTE' }) }));
    const url = URL.createObjectURL(new Blob([lineas.join('\n') + '\n'], { type: 'application/x-ndjson' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `audios_duplicados_${new Date().toISOString().slice(0, 10)}.jsonl`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Importa un seguimiento exportado (de otro navegador o persona): toma decisión y nota por caso.
  const importar = async (file: File) => {
    try {
      const nuevo = { ...seg };
      for (const linea of (await file.text()).split('\n').filter((l) => l.trim())) {
        const r = JSON.parse(linea);
        if (r?.clave && r.decision) nuevo[r.clave] = { decision: r.decision, nota: r.nota, actualizadoEn: r.actualizadoEn };
      }
      setSeg(nuevo);
      guardar(nuevo);
    } catch (e: any) {
      setError(`No se pudo importar: ${e.message}`);
    } finally {
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const lista = casos ?? [];
  const decisionDe = (c: Caso) => seg[c.clave]?.decision ?? 'PENDIENTE';
  const conteo = useMemo(() => {
    const r: Record<string, number> = {};
    for (const c of lista) r[decisionDe(c)] = (r[decisionDe(c)] ?? 0) + 1;
    return r;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lista, seg]);

  // Agrupado por empresa (la del primer audio del caso), para revisar empresa por empresa.
  const porEmpresa = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    const m = new Map<string, { nombre: string; casos: Caso[] }>();
    for (const c of lista) {
      if (filtro !== 'TODOS' && decisionDe(c) !== filtro) continue;
      const textos = c.audios.flatMap((a) => [a.empresaNombre, a.empresaId, a.asesorNombre, String(a.asesoriaId)]);
      if (q && !textos.some((t) => t?.toLowerCase().includes(q))) continue;
      const e = c.audios[0];
      const g = m.get(e.empresaId) ?? { nombre: e.empresaNombre, casos: [] };
      g.casos.push(c);
      m.set(e.empresaId, g);
    }
    return [...m.entries()].sort((a, b) => a[1].nombre.localeCompare(b[1].nombre));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lista, seg, filtro, busqueda]);

  const etiquetaAsesoria = (a: AudioDuplicadoItemDTO) => {
    const act = actividad(ordenPorAsesoria.get(a.asesoriaId));
    return act ? `${act} (#${a.asesoriaId})` : `#${a.asesoriaId}`;
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <h1 className="text-[22px] font-semibold tracking-[-0.03em] text-(--admin-text)">Audios duplicados</h1>
          <p className="mt-1 max-w-2xl text-[13px] text-(--admin-text2)">
            Audios vigentes con la misma duración, de la misma empresa o del mismo asesor. Escucha cada caso antes de decidir
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            ref={inputRef}
            type="file"
            accept=".jsonl,application/x-ndjson"
            className="hidden"
            onChange={(e) => e.target.files?.[0] && void importar(e.target.files[0])}
          />
          <Button variant="secondary" onClick={cargar} disabled={cargando}>
            <RefreshCw className={`w-4 h-4 mr-2 ${cargando ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <Button variant="secondary" onClick={() => inputRef.current?.click()}>
            <FileUp className="w-4 h-4 mr-2" />
            Cargar seguimiento
          </Button>
          <Button variant="secondary" onClick={exportar} disabled={!casos}>
            <Download className="w-4 h-4 mr-2" />
            Descargar seguimiento
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-(--admin-red) bg-(--admin-red-bg) px-3 py-2 text-sm text-(--admin-red)">{error}</div>
      )}
      {!casos && !error && <div className="text-sm text-(--admin-text3)">Cargando audios…</div>}

      {casos && (
        <>
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <div className="flex flex-wrap gap-2">
              {[{ valor: 'TODOS' as const, label: 'Todos' }, ...DECISIONES].map((d) => (
                <button
                  key={d.valor}
                  onClick={() => setFiltro(d.valor)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
                    filtro === d.valor
                      ? 'bg-(--admin-text) text-white'
                      : 'bg-(--admin-surface2) text-(--admin-text2) hover:bg-(--admin-border)'
                  }`}
                >
                  {d.label} ({d.valor === 'TODOS' ? lista.length : (conteo[d.valor] ?? 0)})
                </button>
              ))}
            </div>
            <div className="relative md:ml-auto md:w-72">
              <Search className="w-4 h-4 text-(--admin-text3) absolute left-3 top-2.5" />
              <input
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder="Empresa, NIT, asesor, asesoría…"
                className="w-full bg-(--admin-surface) border border-(--admin-border) rounded-lg pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-(--admin-border2)"
              />
            </div>
          </div>

          {porEmpresa.length === 0 && <div className="text-sm text-(--admin-text3) text-center py-8">No hay casos con estos filtros</div>}

          {porEmpresa.map(([nit, g]) => (
            <section key={nit} className="bg-(--admin-surface) rounded-(--admin-r2) border border-(--admin-border) overflow-hidden">
              <header className="flex items-center justify-between px-4 py-2.5 bg-(--admin-surface2) border-b border-(--admin-border)">
                <div>
                  <span className="font-semibold text-(--admin-text)">{g.nombre}</span>
                  <span className="ml-2 text-xs text-(--admin-text2)">NIT {nit}</span>
                </div>
                <span className="text-xs text-(--admin-text2)">{g.casos.length} caso(s)</span>
              </header>
              <div className="divide-y divide-(--admin-border)">
                {g.casos.map((c) => {
                  const abierto = abiertos.has(c.clave);
                  const s = seg[c.clave];
                  return (
                    <Fragment key={c.clave}>
                      <div className="px-4 py-3 grid md:grid-cols-[1fr_16rem] gap-3 text-xs">
                        <div className="space-y-1.5">
                          <div className="flex flex-wrap items-center gap-2">
                            <Copy className="w-3.5 h-3.5 text-(--admin-text3)" />
                            <b className="text-(--admin-text)">
                              {c.audios.length} audios de {c.duracionFormatted}
                            </b>
                            <Sello>{c.motivo === 'MISMA_EMPRESA' ? 'Misma empresa' : 'Mismo asesor'}</Sello>
                            <button
                              onClick={() =>
                                setAbiertos((prev) => {
                                  const n = new Set(prev);
                                  if (n.has(c.clave)) n.delete(c.clave);
                                  else n.add(c.clave);
                                  return n;
                                })
                              }
                              className="ml-auto flex items-center gap-1 text-(--admin-blue) hover:underline"
                            >
                              {abierto ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                              <Headphones className="w-3 h-3" /> {abierto ? 'Ocultar' : 'Escuchar y comparar'}
                            </button>
                          </div>
                          <ul className="space-y-0.5 text-(--admin-text2)">
                            {c.audios.map((a) => (
                              <li key={a.audioId}>
                                Audio #{a.audioId} · {a.empresaId !== nit ? `${a.empresaNombre} · ` : ''}
                                {etiquetaAsesoria(a)} · subió <b>{a.asesorNombre}</b> · {fecha(a.fechaGrabacion)} · {estadoAudio(a.estado)}
                                {' · '}
                                <Link
                                  to={`/empresas/${a.empresaId}/asesorias/${a.asesoriaId}`}
                                  className="text-(--admin-blue) hover:underline"
                                >
                                  ver asesoría
                                </Link>
                              </li>
                            ))}
                          </ul>
                          {abierto && (
                            <div className="pt-2 space-y-2">
                              {c.audios.map((a) => (
                                <div key={a.audioId} className="rounded-md border border-(--admin-border) p-2">
                                  <div className="mb-1 flex items-center gap-2 text-[11px] text-(--admin-text2)">
                                    <b className="text-(--admin-text)">Audio #{a.audioId}</b> {etiquetaAsesoria(a)} · {a.asesorNombre}
                                    <a
                                      href={a.driveUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="ml-auto flex items-center gap-1 text-(--admin-blue) hover:underline"
                                    >
                                      Abrir en Drive <ExternalLink className="w-3 h-3" />
                                    </a>
                                  </div>
                                  <AudioPlayer
                                    fileIdOrUrl={a.adjunteDelAudio}
                                    initialDuration={a.duracionSeg}
                                    compact
                                    showDriveLink={false}
                                  />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="space-y-1.5">
                          <select
                            value={s?.decision ?? 'PENDIENTE'}
                            onChange={(e) => cambiar(c.clave, { decision: e.target.value as Decision })}
                            className="w-full border border-(--admin-border) rounded-md px-2 py-1 text-xs"
                            aria-label="Decisión"
                          >
                            {DECISIONES.map((d) => (
                              <option key={d.valor} value={d.valor}>
                                {d.label}
                              </option>
                            ))}
                          </select>
                          <textarea
                            defaultValue={s?.nota ?? ''}
                            onBlur={(e) => e.target.value !== (s?.nota ?? '') && cambiar(c.clave, { nota: e.target.value })}
                            placeholder="Nota (p. ej. cuál audio dejar)…"
                            rows={2}
                            className="w-full border border-(--admin-border) rounded-md px-2 py-1 text-xs resize-y"
                          />
                          {s?.actualizadoEn && (
                            <div className="text-[11px] text-(--admin-text3)">
                              Actualizado {new Date(s.actualizadoEn).toLocaleString('es-CO')}
                            </div>
                          )}
                          {esSysadmin && s?.decision === 'DUPLICADO' && (
                            <Link to="/admin/correcciones-asesorias" className="block text-(--admin-blue) hover:underline">
                              Corregir →
                            </Link>
                          )}
                        </div>
                      </div>
                    </Fragment>
                  );
                })}
              </div>
            </section>
          ))}
          <p className="text-[11px] text-(--admin-text3)">Que dos audios duren lo mismo no prueba que sean el mismo archivo</p>
        </>
      )}
    </div>
  );
}
